import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../apiConfig';

const MockPortfolioPage = () => {
  const [mockTrades, setMockTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [solPrice, setSolPrice] = useState(null);
  const [loadingSolPrice, setLoadingSolPrice] = useState(true);
  const [tokenPrices, setTokenPrices] = useState({});

  // Fetch SOL price from CoinGecko
  const fetchSolPrice = async () => {
    try {
      setLoadingSolPrice(true);
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
      if (response.data.solana && response.data.solana.usd) {
        setSolPrice(response.data.solana.usd);
      } else {
        setSolPrice(null);
      }
    } catch (error) {
      console.error('Failed to fetch SOL price:', error);
      setSolPrice(null);
    } finally {
      setLoadingSolPrice(false);
    }
  };

  // Fetch mock trades from backend
  const fetchMockTrades = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      const apiUrl = getApiUrl();
      const response = await axios.get(`${apiUrl}/mock-trades`);
      setMockTrades(response.data || []);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch mock trades:', error);
      setError('Failed to fetch mock trades');
      setMockTrades([]);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Helper function to check if trade has buy transaction icon link
  const hasBuyTransactionIcon = (trade) => {
    return !!trade.transaction_id && !!trade.buy_time;
  };

  // Helper function for holding percentage
  const getHoldingPercentage = (trade) => {
    if (!trade.buy_time) return 0;
    if (!trade.original_amount || trade.original_amount <= 0) {
      return 100;
    }
    const currentAmount = trade.amount || trade.original_amount || 0;
    return Math.round((currentAmount / trade.original_amount) * 100);
  };

  // Fetch token price from DexScreener
  const fetchTokenPrice = async (tokenAddress) => {
    try {
      const response = await axios.get(`https://api.dexscreener.com/latest/dex/tokens/${tokenAddress}`);
      if (response.data && response.data.pairs && response.data.pairs.length > 0) {
        const pair = response.data.pairs[0];
        return parseFloat(pair.priceUsd) || 0;
      }
      return 0;
    } catch (error) {
      console.error(`Failed to fetch price for token ${tokenAddress}:`, error);
      return 0;
    }
  };

  // Fetch prices for all active mock trades
  const fetchTokenPrices = async () => {
    const activeTrades = mockTrades.filter(trade => 
      hasBuyTransactionIcon(trade) && !trade.sell_time && trade.token_address
    );
    
    const pricePromises = activeTrades.map(async (trade) => {
      const price = await fetchTokenPrice(trade.token_address);
      return { tokenAddress: trade.token_address, price };
    });
    
    try {
      const prices = await Promise.all(pricePromises);
      const priceMap = {};
      prices.forEach(({ tokenAddress, price }) => {
        priceMap[tokenAddress] = price;
      });
      setTokenPrices(priceMap);
    } catch (error) {
      console.error('Failed to fetch token prices:', error);
    }
  };

  // Helper function to calculate P/L with fallback to current price calculation
  const calculateProfitLoss = (trade) => {
    let profitLoss = trade.profit_loss;
    
    // For completed trades (with sell_time), always use the stored profit_loss
    if (trade.sell_time) {
      return profitLoss || 0;
    }
    
    // For active trades only: If profit_loss is 0, null, or undefined, calculate it from current price vs buy price
    if ((profitLoss === 0 || profitLoss === null || profitLoss === undefined) && 
        trade.buy_price && trade.buy_price > 0) {
      
      // Try to get real-time price from tokenPrices state
      const realTimePrice = tokenPrices[trade.token_address];
      
      // Use real-time price if available, otherwise use current_price, then buy_price as fallback
      const currentPrice = realTimePrice || trade.current_price || trade.buy_price;
      
      // Calculate P/L if we have a different price than buy price
      if (currentPrice !== trade.buy_price && currentPrice > 0) {
        profitLoss = ((currentPrice - trade.buy_price) / trade.buy_price) * 100;
      } else {
        // If prices are the same or invalid, P/L is 0
        profitLoss = 0;
      }
    }
    
    return profitLoss || 0;
  };

  // Calculate mock trade statistics
  const allCount = mockTrades.length;
  const activeCount = mockTrades.filter(trade => hasBuyTransactionIcon(trade) && !trade.sell_time).length;
  const completedCount = mockTrades.filter(trade => hasBuyTransactionIcon(trade) && (getHoldingPercentage(trade) === 0 || trade.sell_time)).length;
  const pendingCount = mockTrades.filter(trade => trade.status === 'pending').length;
  const nothingCount = mockTrades.filter(trade => !hasBuyTransactionIcon(trade)).length;
  const failedCount = mockTrades.filter(trade => 
    (trade.status === 'failed' || trade.failed) || 
    (trade.buy_time && !hasBuyTransactionIcon(trade)) || 
    (trade.transaction_warning && trade.transaction_warning.includes('failed'))
  ).length;

  // Calculate profit/loss statistics
  const completedTrades = mockTrades.filter(trade => trade.sell_time);
  const profitableTrades = completedTrades.filter(trade => calculateProfitLoss(trade) > 0);
  const lossTrades = completedTrades.filter(trade => calculateProfitLoss(trade) < 0);
  const winRate = completedTrades.length > 0 ? (profitableTrades.length / completedTrades.length * 100) : 0;

  const totalProfitLoss = completedTrades.reduce((sum, trade) => {
    return sum + calculateProfitLoss(trade);
  }, 0);

  const totalProfit = profitableTrades.reduce((sum, trade) => sum + calculateProfitLoss(trade), 0);
  const totalLoss = lossTrades.reduce((sum, trade) => sum + calculateProfitLoss(trade), 0);

  // Calculate mock portfolio value (simulated)
  const getTotalMockPortfolioValue = () => {
    // Calculate portfolio value based on current mock SOL balance
    const currentSolPrice = solPrice || 100; // Fallback to $100 if price not available
    const totalMockSOL = getTotalMockSOL();
    return totalMockSOL * currentSolPrice;
  };

  const getTotalMockSOL = () => {
    // Calculate Total Mock SOL balance properly accounting for all transactions
    
    // Start with initial balance (reset to 0.0 SOL)
    let totalMockSOL = 0.0;
    
    // Add any deposits from localStorage
    const storedDeposits = JSON.parse(localStorage.getItem('mockDeposits') || '[]');
    const totalDeposits = storedDeposits.reduce((sum, deposit) => sum + deposit.amount, 0);
    totalMockSOL += totalDeposits;
    
    // Subtract SOL used for each trade (both active and completed)
    const tradeAmount = 0.015; // Amount used per trade (matches backend STATIC_DEPOSIT_AMOUNT)
    const totalTradesUsed = activeCount + completedCount;
    totalMockSOL -= totalTradesUsed * tradeAmount;
    
    // Add back actual SOL received from completed trades
    completedTrades.forEach(trade => {
      const profitLoss = trade.profit_loss || 0;
      
      // Calculate actual SOL received based on profit/loss
      const actualSolReceived = tradeAmount * (1 + (profitLoss / 100));
      totalMockSOL += actualSolReceived;
    });
    
    // Handle partial sales if they exist
    mockTrades.forEach(trade => {
      if (trade.partial_sales && trade.partial_sales.length > 0) {
        trade.partial_sales.forEach(sale => {
          const profitLoss = sale.profit_loss || 0;
          // Calculate actual SOL received from partial sale
          let actualSolReceived;
          if (sale.amount && sale.amount > 0) {
            // If sale.amount is already calculated correctly, use it
            actualSolReceived = sale.amount;
          } else {
            // Calculate based on profit/loss percentage
            actualSolReceived = tradeAmount * (1 + (profitLoss / 100));
          }
          totalMockSOL += actualSolReceived;
        });
      }
    });
    
    // Ensure balance doesn't go below 0
    return Math.max(0, totalMockSOL);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchMockTrades(true); // Initial load
    fetchSolPrice(); // Fetch SOL price
    
    // Set up periodic refresh
    const interval = setInterval(() => {
      fetchMockTrades(false); // Background refresh
    }, 5000);
    
    // Set up SOL price refresh every 30 seconds
    const solPriceInterval = setInterval(() => {
      fetchSolPrice();
    }, 30000);
    
    return () => {
      clearInterval(interval);
      clearInterval(solPriceInterval);
    };
  }, []);

  // Fetch token prices when mock trades change
  useEffect(() => {
    if (mockTrades.length > 0) {
      fetchTokenPrices();
    }
  }, [mockTrades]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-16 w-16 border-2 border-transparent" style={{ borderTopColor: '#22c55e', borderRightColor: '#22c55e' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 text-lg">{error}</p>
        <button 
          onClick={fetchMockTrades}
          className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mock Portfolio Overview */}
      <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Mock Portfolio Overview</h2>
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-900/50 text-yellow-400">
            SIMULATION MODE
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Total Mock Portfolio Value</div>
            <div className="text-2xl font-bold text-green-400">
              ${getTotalMockPortfolioValue().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Simulated Value
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">SOL Price (Live)</div>
            <div className="text-2xl font-bold text-blue-400">
              {loadingSolPrice ? 'Loading...' : solPrice ? `$${solPrice.toFixed(2)}` : 'N/A'}
            </div>
            <div className="text-xs text-slate-500 mt-1">Real-time Price</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Total Mock SOL</div>
            <div className="text-2xl font-bold text-purple-400">
              {getTotalMockSOL().toFixed(4)}
            </div>
            <div className="text-xs text-slate-500 mt-1">Simulated Balance</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Mock Trades</div>
            <div className="text-2xl font-bold text-yellow-400">
              {allCount}
            </div>
            <div className="text-xs text-slate-500 mt-1">Total Simulated</div>
          </div>
        </div>
      </div>

      {/* Mock Trading Info Banner */}
      <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
        <div className="flex items-center space-x-2">
          <span className="text-yellow-400 text-lg">💼</span>
          <div>
            <h4 className="text-yellow-400 font-semibold">Mock Portfolio Simulation</h4>
            <p className="text-yellow-300 text-sm">
              This portfolio shows simulated trading results with no real financial risk. 
              All values are calculated based on mock trades and simulated SOL prices.
            </p>
            <p className="text-yellow-200 text-xs mt-1">
              Use this data to understand potential portfolio performance before going live.
            </p>
          </div>
        </div>
      </div>

      {/* Mock Portfolio Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* Mock Trading Statistics Card */}
        <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            📊 Mock Trading Statistics
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Win Rate:</span>
              <span className={`font-semibold ${winRate >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                {winRate.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Profitable Trades:</span>
              <span className="text-green-400 font-semibold">{profitableTrades.length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Loss Trades:</span>
              <span className="text-red-400 font-semibold">{lossTrades.length}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-slate-600">
              <span className="text-white font-semibold">Net P&L:</span>
              <span className={`font-bold text-lg ${totalProfitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {totalProfitLoss >= 0 ? '+' : ''}{totalProfitLoss.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>

        {/* Mock Trade Counts Card */}
        <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            📈 Mock Trade Overview
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">All Mock Trades:</span>
              <span className="text-blue-400 font-semibold">{allCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Active:</span>
              <span className="text-purple-400 font-semibold">{activeCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Completed:</span>
              <span className="text-green-400 font-semibold">{completedCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Pending:</span>
              <span className="text-yellow-400 font-semibold">{pendingCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Failed:</span>
              <span className="text-red-400 font-semibold">{failedCount}</span>
            </div>
          </div>
        </div>

        {/* Mock Portfolio Performance Card */}
        <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            💰 Mock Portfolio Performance
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Portfolio Value:</span>
              <span className="text-green-400 font-semibold">
                ${getTotalMockPortfolioValue().toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Mock SOL:</span>
              <span className="text-purple-400 font-semibold">
                {getTotalMockSOL().toFixed(4)} SOL
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Avg Trade Size:</span>
              <span className="text-blue-400 font-semibold">
                0.001 SOL
              </span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-slate-600">
              <span className="text-white font-semibold">Performance:</span>
              <span className={`font-bold text-lg ${totalProfitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {totalProfitLoss >= 0 ? 'Profitable' : 'Loss-Making'}
              </span>
            </div>
          </div>
        </div>

      </div>

      {/* Detailed Mock Portfolio Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Mock Active Trades */}
        <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
          <h3 className="text-xl font-semibold text-white mb-4">Mock Active Trades</h3>
          {activeCount === 0 ? (
            <p className="text-slate-400 text-center py-8">No active mock trades</p>
          ) : (
            <div className="space-y-3">
              {mockTrades.filter(trade => hasBuyTransactionIcon(trade) && !trade.sell_time).slice(0, 5).map((trade, index) => (
                <div key={index} className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-400 font-medium">
                      {trade.symbol || trade.name || 'Unknown Token'}
                    </span>
                    <span className="text-yellow-400 text-xs bg-yellow-900/30 px-2 py-1 rounded">
                      MOCK
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-white font-semibold">{trade.amount || 0} SOL</div>
                      <div className="text-slate-400 text-sm">
                        {loadingSolPrice ? 'Loading...' : solPrice ? `$${((trade.amount || 0) * solPrice).toFixed(2)}` : 'N/A'} (Simulated)
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-400 text-xs">P&L</div>
                      <div className={`text-sm font-semibold ${calculateProfitLoss(trade) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {calculateProfitLoss(trade) >= 0 ? '+' : ''}{calculateProfitLoss(trade).toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {activeCount > 5 && (
                <p className="text-slate-400 text-center text-sm">
                  ... and {activeCount - 5} more active mock trades
                </p>
              )}
            </div>
          )}
        </div>

        {/* Mock Profit/Loss Breakdown */}
        <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
          <h3 className="text-xl font-semibold text-white mb-4">Mock P&L Breakdown</h3>
          <div className="space-y-4">
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-green-400 font-semibold">Total Mock Profits</span>
                <span className="text-green-400 font-bold text-lg">+{totalProfit.toFixed(2)}%</span>
              </div>
              <div className="text-green-300 text-sm mt-1">
                From {profitableTrades.length} profitable mock trades
              </div>
            </div>
            
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-red-400 font-semibold">Total Mock Losses</span>
                <span className="text-red-400 font-bold text-lg">{totalLoss.toFixed(2)}%</span>
              </div>
              <div className="text-red-300 text-sm mt-1">
                From {lossTrades.length} loss mock trades
              </div>
            </div>
            
            <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-white font-semibold">Mock Net Result</span>
                <span className={`font-bold text-xl ${totalProfitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totalProfitLoss >= 0 ? '+' : ''}{totalProfitLoss.toFixed(2)}%
                </span>
              </div>
              <div className="text-slate-400 text-sm mt-1">
                Overall mock trading performance
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Mock Trading Strategy Analysis */}
      <div className="backdrop-blur-sm rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
        <h3 className="text-xl font-semibold text-white mb-6">Mock Trading Strategy Analysis</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-400 mb-2">{winRate.toFixed(1)}%</div>
            <div className="text-slate-400 text-sm">Mock Win Rate</div>
            <div className="text-xs text-slate-500 mt-1">
              {winRate >= 70 ? 'Excellent' : winRate >= 50 ? 'Good' : 'Needs Improvement'}
            </div>
          </div>
          
          <div className="text-center">
            <div className={`text-3xl font-bold mb-2 ${totalProfitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {totalProfitLoss >= 0 ? '+' : ''}{totalProfitLoss.toFixed(2)}%
            </div>
            <div className="text-slate-400 text-sm">Mock Net P&L</div>
            <div className="text-xs text-slate-500 mt-1">
              {totalProfitLoss >= 0 ? 'Profitable Strategy' : 'Loss-Making Strategy'}
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-400 mb-2">{allCount}</div>
            <div className="text-slate-400 text-sm">Total Mock Trades</div>
            <div className="text-xs text-slate-500 mt-1">
              {allCount >= 50 ? 'Good Sample Size' : allCount >= 20 ? 'Moderate Sample' : 'Small Sample'}
            </div>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-slate-900/50 border border-slate-600 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-3">Strategy Recommendations</h4>
          <div className="space-y-2">
            {winRate >= 70 && totalProfitLoss >= 0 ? (
              <p className="text-green-400 text-sm">
                ✅ Your mock strategy shows excellent performance. Consider testing with small live amounts.
              </p>
            ) : winRate >= 50 && totalProfitLoss >= 0 ? (
              <p className="text-yellow-400 text-sm">
                ⚠️ Your mock strategy shows moderate performance. Consider refining entry/exit criteria.
              </p>
            ) : (
              <p className="text-red-400 text-sm">
                ❌ Your mock strategy needs improvement. Analyze failed trades and adjust parameters.
              </p>
            )}
            
            <p className="text-blue-400 text-sm">
              🔄 Continue mock trading to gather more data before making live trades.
            </p>
            
            <p className="text-slate-400 text-sm">
              📊 Use this mock portfolio data to understand potential real-world performance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockPortfolioPage;
